import { ApiService } from './api-service/api.service';
import { IPromise } from 'angular';
import { IdentificationDocument } from '../entities/cv/identification-document';
import { ISocialNetworks, IUserProfile } from '../entities/profile';
import {
  Biometrics,
  Cv,
  Diploma,
  DpInfo,
  Educations,
  IAdditionalInfo,
  JobRequirements
} from '../entities/cv';
import { Certificates } from '../entities/cv/certificates';
import {
  IMedicalAndVaccination,
  MedicalAndVaccination
} from '../entities/cv/medical-and-vaccination';
import { Skills } from '../entities/cv/skills';
import { WellsSpecifications } from '../entities/cv/wells-specifications';
import { Reference } from '../entities/cv/reference';
import { Resume } from '../entities/cv/resume';
import { LocationAndVisas } from '../entities/cv/location-and-visas';
import { SeaJobExperience } from '../entities/cv/sea-job-experience';
import {
  IVesselSection,
  IVesselType,
  JobRankService
} from './job-rank/job-rank.service';
import { JobPositionService } from './job-position/job-position.service';
import {
  EmploymentHistory,
  IEmploymentHistory
} from '../entities/cv/employment-history';
import { Certificate } from '../entities/certificate';
import {
  CvPersonalInfo,
  ICvPersonalMainInfo
} from '../entities/cv/cv-personal-info';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';
import { RoleService } from './role/role.service';
import { IDataMap } from '../interfaces/data-map.interface';
import { ModalService } from '../../components/modals/gmc-modal/gmc-modal.service';
import { IAdditionalInfoModalData } from '../../components/modals/additional-info-modal/additional-info-modal-data.interface';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { IContinents } from '../entities/cv/continents';
import { UserRoles } from './role/role-statuses';
import { Helpers } from '../../helpers';

export class CvService {
  static $inject = [
    'ApiService',
    'JobRankService',
    'JobPositionService',
    'RoleService',
    'ModalService'
  ];

  hasBonus = new BehaviorSubject<boolean>(false);
  userRole: any;
  cvCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  helpers = Helpers;

  private getPath = '/user/getMyProfile';
  private updatePath = '/user/updateCandidateProfile';
  private updateBasicProfilePath = '/user/updateBasicProfile';
  private _onUpdateCv = new Subject<boolean>();

  constructor(
    private apiService: ApiService,
    private jobService: JobRankService,
    private positionService: JobPositionService,
    private roleService: RoleService,
    private modalService: ModalService
  ) {}

  get onUpdateCv(): Observable<boolean> {
    return this._onUpdateCv.asObservable();
  }

  getNewCv() {
    this.apiService.get('/cv/count/new').then((res) => {
      this.cvCount.next(res.count);
    });
  }

  getFillPoints(): IPromise<number> {
    return this.apiService
      .get<{ points: number }>('/user/checkCandidateProfileFillPoints')
      .then((data) => {
        return data.points || 0;
      });
  }

  getPersonalInfo(): IPromise<CvPersonalInfo> {
    return this.apiService.get<IUserProfile>(this.getPath).then((data) => {
      this.userRole = data.role;
      return new CvPersonalInfo(data);
    });
  }

  updatePersonalInfo(data: CvPersonalInfo): IPromise<any> {
    const update = {
      ...data.mainInfo,
      social_networks: {
        ...data.social_networks
      },
      candidate: {
        cv: {
          additionalPersonalInfo: {
            ...data.additionalInfo
          },
          nationality: data.mainInfo.nationality,
          closest_airport: data.mainInfo.closest_airport,
          photo_base64:undefined
        }
      }
    };

    return this.apiService
      .post(this.updateBasicProfilePath, { personal_info: update })
      .then((res) => {
        this._onUpdateCv.next(true);
        return res;
      });
  }

  updateMainPersonalInfo(data: ICvPersonalMainInfo): IPromise<any> {
    const update = {
      ...data,
      birthday: data.birthday.endOf('d').format(),
      photo_base64:undefined,
      candidate: {
        cv: {
          nationality: data.nationality,
          closest_airport: data.closest_airport
        }
      }
    };
    return this.apiService
      .post(this.updateBasicProfilePath, { personal_info: update })
      .then((res) => {
        this._onUpdateCv.next(true);
        return res;
      });
  }

  updateAdditionalInfo(data: IAdditionalInfo): IPromise<any> {
    const update = {
      candidate: {
        cv: {
          additionalPersonalInfo: {
            ...data
          }
        }
      }
    };
    return this.apiService
      .post(this.updateBasicProfilePath, { personal_info: update })
      .then((res) => {
        this._onUpdateCv.next(true);
        return res;
      });
  }

  updateSocials(data: ISocialNetworks): IPromise<any> {
    const update = {
      social_networks: {
        ...data
      }
    };
    return this.apiService
      .post(this.updateBasicProfilePath, { personal_info: update })
      .then((res) => {
        this._onUpdateCv.next(true);
        return res;
      });
  }

  getIdentificationDocuments(): IPromise<IdentificationDocument[]> {
    return this.apiService.get<IUserProfile>(this.getPath).then((data) => {
      this.userRole = data.role;
      const { identificationDocument } = data.candidate.cv;
      return identificationDocument
        ? identificationDocument.map((item) => new IdentificationDocument(item))
        : [];
    });
  }

  updateIdentificationDocuments(data: IdentificationDocument[]): IPromise<any> {
    const update = {
      candidate: {
        cv: {
          identificationDocument: data
        }
      }
    };
    return this.apiService
      .post(this.updatePath, this.helpers.removeEmptyAndParseDate(update))
      .then((res) => {
        this._onUpdateCv.next(true);
        // this.hasBonus.next(res.bonus.show_modal);
        this.hasBonus.next(res.bonus.show_modal);
        return res;
      });
  }

  getDiplomas(): IPromise<Diploma[]> {
    return this.apiService.get<IUserProfile>(this.getPath).then((data) => {
      this.userRole = data.role;
      const { diplomaAndEndorsement } = data.candidate.cv;
      return diplomaAndEndorsement
        ? diplomaAndEndorsement.map((item) => new Diploma(item))
        : [];
    });
  }

  updateDiplomas(data: Diploma[]): IPromise<any> {
    const update = {
      candidate: {
        cv: {
          diplomaAndEndorsement: data
        }
      }
    };
    return this.apiService.post(this.updatePath, this.helpers.removeEmptyAndParseDate(update)).then((res) => {
      this._onUpdateCv.next(true);
      this.hasBonus.next(res.bonus.show_modal);
      return res;
    });
  }

  getCertificates(): IPromise<Certificates> {
    return this.apiService.get<IUserProfile>(this.getPath).then((data) => {
      this.userRole = data.role;
      const { certificates } = data.candidate.cv;
      return new Certificates(certificates);
    });
  }

  updateCertificates(data: Certificates): IPromise<any> {
    const update = {
      candidate: {
        cv: {
          certificates: data
        }
      }
    };
    return this.apiService.post(this.updatePath, this.helpers.removeEmptyAndParseDate(update)).then((res) => {
      this._onUpdateCv.next(true);
      this.hasBonus.next(res.bonus.show_modal);
      return res;
    });
  }

  getMedicalAndVaccination(): IPromise<MedicalAndVaccination> {
    return this.apiService.get<IUserProfile>(this.getPath).then((data) => {
      this.userRole = data.role;
      const { medical_and_vaccination } = data.candidate.cv;
      return new MedicalAndVaccination(medical_and_vaccination);
    });
  }

  updateMedicalAndVaccination(data: IMedicalAndVaccination) {
    const update = {
      candidate: {
        cv: {
          medical_and_vaccination: data
        }
      }
    };
    return this.apiService.post(this.updatePath, this.helpers.removeEmptyAndParseDate(update)).then((res) => {
      this._onUpdateCv.next(true);
      this.hasBonus.next(res.bonus.show_modal);
      return res;
    });
  }

  getDpInfo(): IPromise<DpInfo> {
    return this.apiService.get<IUserProfile>(this.getPath).then((data) => {
      this.userRole = data.role;
      const { dp_info } = data.candidate.cv;
      return new DpInfo(dp_info);
    });
  }

  updateDpInfo(data: DpInfo): IPromise<any> {
    const update = {
      candidate: {
        cv: {
          dp_info: data
        }
      }
    };
    return this.apiService.post(this.updatePath, this.helpers.removeEmptyAndParseDate(update)).then((res) => {
      this._onUpdateCv.next(true);
      this.hasBonus.next(res.bonus.show_modal);
      return res;
    });
  }

  getSkills(): IPromise<Skills> {
    return this.apiService.get<IUserProfile>(this.getPath).then((data) => {
      this.userRole = data.role;
      const vesselSectionsMap: IDataMap<
        IVesselSection
      > = this.jobService.getVesselSectionsMap();
      const { skills_and_ability } = data.candidate.cv;
      return new Skills(skills_and_ability, vesselSectionsMap);
    });
  }

  updateSkills(data: Skills): IPromise<any> {
    const update: IUserProfile = {
      candidate: {
        cv: {
          skills_and_ability: data
        }
      }
    };
    return this.apiService.post(this.updatePath, this.helpers.removeEmptyAndParseDate(update)).then((res) => {
      this._onUpdateCv.next(true);
      this.hasBonus.next(res.bonus.show_modal);
      return res;
    });
  }

  getWellsSpec(): IPromise<WellsSpecifications> {
    return this.apiService.get<IUserProfile>(this.getPath).then((data) => {
      this.userRole = data.role;
      const { wells_specification } = data.candidate.cv;
      return new WellsSpecifications(wells_specification);
    });
  }

  updateWellsSpec(data: WellsSpecifications): IPromise<any> {
    const update = {
      candidate: {
        cv: {
          wells_specification: data
        }
      }
    };
    return this.apiService.post(this.updatePath, this.helpers.removeEmptyAndParseDate(update)).then((res) => {
      this._onUpdateCv.next(true);
      this.hasBonus.next(res.bonus.show_modal);
      return res;
    });
  }

  getBiometrics(): IPromise<Biometrics> {
    return this.apiService.get<IUserProfile>(this.getPath).then((data) => {
      this.userRole = data.role;
      const { biometrics } = data.candidate.cv;
      return new Biometrics(biometrics);
    });
  }

  updateBiometrics(data: Biometrics): IPromise<any> {
    const update = {
      candidate: {
        cv: {
          biometrics: data
        }
      }
    };
    return this.apiService.post(this.updatePath, this.helpers.removeEmptyAndParseDate(update)).then((res) => {
      this._onUpdateCv.next(true);
      this.hasBonus.next(res.bonus.show_modal);
      return res;
    });
  }

  getReferences(): IPromise<Reference[]> {
    return this.apiService.get<IUserProfile>(this.getPath).then((data) => {
      this.userRole = data.role;
      const { reference } = data.candidate.cv;
      return reference ? reference.map((item) => new Reference(item)) : [];
    });
  }

  updateReferences(data: Reference[]): IPromise<any> {
    const update = {
      candidate: {
        cv: {
          reference: data
        }
      }
    };
    return this.apiService.post(this.updatePath, this.helpers.removeEmptyAndParseDate(update)).then((res) => {
      this._onUpdateCv.next(true);
      this.hasBonus.next(res.bonus.show_modal);
      return res;
    });
  }

  getResume(): IPromise<Resume> {
    return this.apiService.get<IUserProfile>(this.getPath).then((data) => {
      this.userRole = data.role;
      const { my_resume } = data.candidate.cv;
      return new Resume(my_resume);
    });
  }

  updateResume(data: Resume): IPromise<any> {
    const update = {
      candidate: {
        cv: {
          my_resume: data
        }
      }
    };
    return this.apiService.post(this.updatePath, this.helpers.removeEmptyAndParseDate(update)).then((res) => {
      this._onUpdateCv.next(true);
      this.hasBonus.next(res.bonus.show_modal);
      return res;
    });
  }

  getEducations(): IPromise<Educations> {
    return this.apiService.get<IUserProfile>(this.getPath).then((data) => {
      this.userRole = data.role;
      const { education } = data.candidate.cv;
      return new Educations(education);
    });
  }

  updateEducations(data: Educations): IPromise<any> {
    const update = {
      candidate: {
        cv: {
          education: data
        }
      }
    };
    return this.apiService.post(this.updatePath, this.helpers.removeEmptyAndParseDate(update)).then((res) => {
      this._onUpdateCv.next(true);
      this.hasBonus.next(res.bonus.show_modal);
      return res;
    });
  }

  getLocationAndVisas(): IPromise<LocationAndVisas> {
    return this.apiService.get<IUserProfile>(this.getPath).then((data) => {
      this.userRole = data.role;
      const { location_and_visas } = data.candidate.cv;
      return new LocationAndVisas(location_and_visas);
    });
  }

  getContinents(): IPromise<any> {
    return this.apiService
      .get('/pages/all/continents')
      .then((res: { continents: IContinents }) => {
        return res.continents;
      })
      .catch((err) => {
        throw new Error(err);
      });
  }

  updateLocationAndVisas(data: LocationAndVisas): IPromise<any> {
    const update = {
      candidate: {
        cv: {
          location_and_visas: data
        }
      }
    };
    return this.apiService.post(this.updatePath, this.helpers.removeEmptyAndParseDate(update)).then((res) => {
      this._onUpdateCv.next(true);
      this.hasBonus.next(res.bonus.show_modal);
      return res;
    });
  }

  getCurrentJobMonth(): IPromise<number> {
    return this.apiService.get<IUserProfile>(this.getPath).then((data) => {
      this.userRole = data.role;
      return data.candidate.current_job_month;
    });
  }

  getSeaServices(): IPromise<SeaJobExperience[]> {
    return this.apiService.get<IUserProfile>(this.getPath).then((data) => {
      this.userRole = data.role;
      const { sea_services } = data.candidate.cv;
      const vesselTypesMap = this.jobService.getVesselTypesMap();
      return sea_services
        ? sea_services.map((item) => {
            const experience = new SeaJobExperience(item);
            const vesselType: IVesselType =
              vesselTypesMap[experience.vessel_type];
            experience.vessel_type_name = vesselType ? vesselType.name : '';
            return experience;
          })
        : [];
    });
  }

  updateSeaServices(data: SeaJobExperience[]): IPromise<any> {
    const update = {
      candidate: {
        cv: {
          sea_services: data,
          job_experience_type: 'sea_services'
        }
      }
    };
    return this.apiService.post(this.updatePath, this.helpers.removeEmptyAndParseDate(update)).then((res) => {
      this._onUpdateCv.next(true);
      this.hasBonus.next(res.bonus.show_modal);
      return res;
    });
  }

  getEmploymentHistory(): IPromise<EmploymentHistory> {
    return this.apiService.get<IUserProfile>(this.getPath).then((data) => {
      this.userRole = data.role;
      const {
        sea_services,
        job_experience,
        job_experience_type
      } = data.candidate.cv;
      const { current_job_month } = data.candidate;
      const item: IEmploymentHistory = {
        job_experience,
        sea_services,
        job_experience_type,
        current_job_month
      };
      const empHistory = new EmploymentHistory(item);
      const vesselTypesMap = this.jobService.getVesselTypesMap();
      empHistory.sea_services.forEach((experience) => {
        const vesselType: IVesselType = vesselTypesMap[experience.vessel_type];
        experience.vessel_type_name = vesselType ? vesselType.name : '';
      });
      // empHistory.job_experience.forEach(( experience ) => {
      //   experience.
      // });
      return empHistory;
    });
  }

  updateEmploymentHistory(data: EmploymentHistory): IPromise<any> {
    const update: IUserProfile = {
      candidate: {
        cv: {
          sea_services: data.sea_services,
          job_experience: data.job_experience,
          job_experience_type: data.job_experience_type
        }
      }
    };
    return this.apiService.post(this.updatePath, this.helpers.removeEmptyAndParseDate(update)).then((res) => {
      this._onUpdateCv.next(true);
      this.hasBonus.next(res.bonus.show_modal);
      return res;
    });
  }

  getTrainingAndCourses(): IPromise<Certificate[]> {
    return this.apiService.get<IUserProfile>(this.getPath).then((data) => {
      this.userRole = data.role;
      const { training_and_courses } = data.candidate.cv;
      return training_and_courses
        ? training_and_courses.map((item) => new Certificate(item))
        : [];
    });
  }

  updateTrainingAndCourses(data: Certificate[]): IPromise<any> {
    const update = {
      candidate: {
        cv: {
          training_and_courses: data
        }
      }
    };
    return this.apiService.post(this.updatePath, this.helpers.removeEmptyAndParseDate(update)).then((res) => {
      this._onUpdateCv.next(true);
      this.hasBonus.next(res.bonus.show_modal);
      return res;
    });
  }

  getJobRequrements(): IPromise<JobRequirements> {
    return this.apiService.get<IUserProfile>(this.getPath).then((data) => {
      this.userRole = data.role;
      const { job_requirements } = data.candidate.cv;
      const vesselTypesMap = this.jobService.getVesselTypesMap();
      const result = new JobRequirements(job_requirements, data.role);
      result.additional.forEach((job) => {
        job.vesselTypeName = vesselTypesMap[job.vessel_type]
          ? vesselTypesMap[job.vessel_type].name
          : '';
      });
      result.current.vesselTypeName = vesselTypesMap[result.current.vessel_type]
        ? vesselTypesMap[result.current.vessel_type].name
        : '';
      return result;
    });
  }

  getCvView(id: string): IPromise<any> {
    const params = {
      id
    };
    return this.apiService.get<any>('/user/getCv', { params }).then((data) => {
      return data;
    });
  }

  downloadAll(id: string, part: string) {
    const params = {
      id: id,
      part: part
    };
    return this.apiService
      .get('/user/downloadCVAttachment', {
        params,
        responseType: 'arraybuffer'
      })
      .then((res) => {
        return res;
      });
  }

  deleteAttachment(item: string) {
    const params = {
      filename: item
    };
    return this.apiService.delete('/user/deleteFile/', { params });
  }

  updateJobRequrements(data: JobRequirements): IPromise<any> {
    const update = {
      candidate: {
        cv: {
          job_requirements: data
        }
      }
    };
    return this.apiService.post(this.updatePath, this.helpers.removeEmptyAndParseDate(update)).then((res) => {
      this._onUpdateCv.next(true);
      this.hasBonus.next(res.bonus.show_modal);
      return res;
    });
  }
}



// WEBPACK FOOTER //
// ./app/core/services/cv.service.ts